import { RadioGroup as RadioGroupAria, Text } from "react-aria-components";
import type { RadioGroupProps, ValidationResult } from "react-aria-components";
import React from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/helpers/cn";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { testLocator } from "@/lib/helpers/test-locator";

import { FieldError2 } from "../atoms/FieldError";
import { Label2 } from "../atoms/Label";

export type RadioGroup2Props = {
  children?: ReactNode;
  classNameChildren?: string;
  classNameWrapper?: string;
  description?: string;
  errorMessage?: ((validation: ValidationResult) => string) | string;
  hasStrongLabel?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  // "display" renders only the selected Radio2's label as static text, no
  // radio inputs — for read-only contexts (e.g. note display views).
  // Distinct from react-aria's isReadOnly, which keeps a focusable,
  // still-a-form-control radio group; "display" has no radio semantics at all.
  mode?: "display" | "edit";
  testId?: string;
} & Omit<RadioGroupProps, "children">;

export function RadioGroup2({
  children,
  classNameChildren,
  description,
  errorMessage,
  hasStrongLabel,
  isInvalid,
  isRequired,
  label,
  mode = "edit",
  name,
  onChange,
  testId,
  value,
  ...props
}: RadioGroup2Props) {
  if (mode === "display") {
    const selectedLabel = React.Children.toArray(children).find(
      (child): child is React.ReactElement<{ children?: ReactNode; value: string }> =>
        React.isValidElement(child) && child.props.value === value,
    )?.props.children;

    return (
      <div
        className="tw:flex tw:flex-col tw:gap-1.5"
        {...getAutomationDataAttr(testId)}
        {...testLocator(testId)}
      >
        <Label2
          className={hasStrongLabel ? "tw:font-bold" : ""}
          isRequired={isRequired}
          label={label}
        />

        <span className="body-md">{typeof selectedLabel === "string" ? selectedLabel : "—"}</span>

        {description && (
          <Text slot="description">
            <small>{description}</small>
          </Text>
        )}
      </div>
    );
  }

  return (
    <RadioGroupAria
      className="tw:flex tw:flex-col tw:gap-3"
      isInvalid={isInvalid}
      name={name}
      onChange={onChange}
      value={value}
      {...testLocator(testId)}
      {...props}
    >
      <Label2
        className={hasStrongLabel ? "tw:font-bold" : ""}
        isInvalid={isInvalid}
        isRequired={isRequired}
        label={label}
      />

      <div className={cn("tw:flex tw:flex-col tw:gap-2 tw:[&>label]:mb-0!", classNameChildren)}>
        {children}
      </div>

      {description && (
        <Text slot="description">
          <small>{description}</small>
        </Text>
      )}

      <FieldError2 {...testLocator(testId && `${testId}-error`)}>{errorMessage}</FieldError2>
    </RadioGroupAria>
  );
}
